import React from 'react';
import { Button, Tag } from 'antd';
import { FiExternalLink } from 'react-icons/fi';
import { AirflowTaskInstance } from '../../types';
import { displayValue, formatDateTime, formatDurationSeconds, TaskInstanceStateBadge } from '../../utils';
import { DetailRow } from '../../detail-row';
import { useDetailsTab } from './use-details-tab';
import styles from '../../styles.module.scss';

type DetailsTabProps = {
    dagId: string;
    runId: string;
    taskId: string;
    mapIndex: number;
    taskInstance: AirflowTaskInstance | null;
    isLoading: boolean;
    refreshKey: number;
};

/*
 * DAG-run-task detail's Details tab — task-instance metadata plus operator-provided Extra Links
 */
export function DetailsTab({ dagId, runId, taskId, mapIndex, taskInstance, isLoading, refreshKey }: DetailsTabProps) {
    const { availableLinks, isLoadingLinks } = useDetailsTab(dagId, runId, taskId, mapIndex, refreshKey);

    if (isLoading || !taskInstance) return null;

    return (
        <div>
            {(isLoadingLinks || availableLinks.length > 0) && (
                <div className={styles.card} style={{ marginBottom: 16 }}>
                    <h3 style={{ margin: '0 0 8px', color: '#fff', fontSize: 14, fontWeight: 700, letterSpacing: 0.25 }}>Extra Links</h3>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {availableLinks.map(([label, url]) => (
                            <Button key={label} onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}>
                                <FiExternalLink style={{ marginRight: 6 }} />
                                {label}
                            </Button>
                        ))}
                    </div>
                </div>
            )}
            <div className={styles.card}>
                <DetailRow label="State">
                    <TaskInstanceStateBadge state={taskInstance.state} />
                </DetailRow>
                <DetailRow label="Task ID">{taskInstance.task_id}</DetailRow>
                <DetailRow label="Run ID">{taskInstance.dag_run_id}</DetailRow>
                <DetailRow label="Map Index">{(taskInstance.map_index ?? -1) >= 0 ? taskInstance.map_index : '—'}</DetailRow>
                <DetailRow label="Operator">
                    {taskInstance.operator_name ?? taskInstance.operator ? <Tag>{taskInstance.operator_name ?? taskInstance.operator}</Tag> : '—'}
                </DetailRow>
                <DetailRow label="Start Date">{formatDateTime(taskInstance.start_date)}</DetailRow>
                <DetailRow label="End Date">{formatDateTime(taskInstance.end_date)}</DetailRow>
                <DetailRow label="Duration">{formatDurationSeconds(taskInstance.duration)}</DetailRow>
                <DetailRow label="Try Number">{taskInstance.try_number ?? 1}</DetailRow>
                <DetailRow label="Max Tries">{displayValue(taskInstance.max_tries)}</DetailRow>
                <DetailRow label="Pool">{displayValue(taskInstance.pool)}</DetailRow>
                <DetailRow label="Pool Slots">{displayValue(taskInstance.pool_slots)}</DetailRow>
                <DetailRow label="Queue">{displayValue(taskInstance.queue)}</DetailRow>
                <DetailRow label="Priority Weight">{displayValue(taskInstance.priority_weight)}</DetailRow>
                <DetailRow label="Hostname">{displayValue(taskInstance.hostname)}</DetailRow>
                <DetailRow label="Unix Name">{displayValue(taskInstance.unixname)}</DetailRow>
                <DetailRow label="PID">{displayValue(taskInstance.pid)}</DetailRow>
                <DetailRow label="Queued When">{formatDateTime(taskInstance.queued_when)}</DetailRow>
                <DetailRow label="Scheduled When">{formatDateTime(taskInstance.scheduled_when)}</DetailRow>
                <DetailRow label="Logical Date">{formatDateTime(taskInstance.execution_date ?? taskInstance.logical_date)}</DetailRow>
                <DetailRow label="Note">{displayValue(taskInstance.note)}</DetailRow>
                <DetailRow label="Executor Config">
                    {taskInstance.executor_config ? <pre style={{ margin: 0, maxHeight: 160, overflow: 'auto' }}>{taskInstance.executor_config}</pre> : '—'}
                </DetailRow>
            </div>
        </div>
    );
}
